// Extract the model's FINAL JSON answer from a chat reply that may include
// visible chain-of-thought before it. Reasoning-capable models
// (the Fireworks Qwen/GLM paths) sometimes narrate first, quoting JSON examples from the prompt
// (e.g. the empty {"patch": {}, "notes": []} envelope), then emit the real
// answer in a ```json fence, then keep narrating – occasionally truncated by
// max_tokens. Taking the FIRST brace in the reply therefore grabs a quoted
// example instead of the answer; the answer is the LAST parseable object, and
// requiredKey guards against tail fragments that merely look like objects.

function stripThinkBlocks(text) {
  // Unclosed <think> (truncated reply) swallows to the end on purpose: what
  // follows an unfinished thought is never the final answer.
  return text.replace(/<think>[\s\S]*?(?:<\/think>|$)/gi, ' ')
}

// Every balanced top-level {...} in the text, in order of appearance. A
// trailing unbalanced object (truncated reply) is included last so callers can
// still attempt a cleanup parse on it.
function balancedObjects(source) {
  const found = []
  let depth = 0
  let start = -1
  let inString = false
  let escape = false
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i]
    if (inString) {
      if (escape) escape = false
      else if (char === '\\') escape = true
      else if (char === '"') inString = false
      continue
    }
    if (char === '"') {
      // Only JSON strings (inside an object) matter; prose quotes at depth 0
      // must not swallow text.
      if (depth > 0) inString = true
    } else if (char === '{') {
      if (depth === 0) start = i
      depth += 1
    } else if (char === '}' && depth > 0) {
      depth -= 1
      if (depth === 0 && start !== -1) {
        found.push(source.slice(start, i + 1))
        start = -1
      }
    }
  }
  if (depth > 0 && start !== -1) found.push(source.slice(start))
  return found
}

function parseCandidate(candidate) {
  const attempts = [candidate, candidate.replace(/,\s*([}\]])/g, '$1')]
  for (const attempt of attempts) {
    try {
      const parsed = JSON.parse(attempt)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch {
      // Try the next generic cleanup.
    }
  }
  return null
}

// Constrained JSON decoding on the provider occasionally blanks the keys
// (values intact, every key ""). Such an object parses fine but is useless –
// callers must fall back rather than surface fragments of it.
function hasBlankKeys(value) {
  if (Array.isArray(value)) return value.some(hasBlankKeys)
  if (value && typeof value === 'object') {
    return Object.entries(value).some(([key, child]) => key === '' || hasBlankKeys(child))
  }
  return false
}

export function extractFinalJsonObject(content, { requiredKey = null } = {}) {
  const text = stripThinkBlocks(String(content || '')).replace(/```(?:json)?/gi, ' ')
  const candidates = balancedObjects(text)
  let lastParseable = null
  for (let i = candidates.length - 1; i >= 0; i -= 1) {
    const parsed = parseCandidate(candidates[i])
    if (!parsed || hasBlankKeys(parsed)) continue
    if (!lastParseable) lastParseable = parsed
    if (!requiredKey || requiredKey in parsed) return parsed
  }
  return lastParseable
}
